/**
 * MyUsagePage
 *
 * Personal usage dashboard for operators and staff.
 * Shows individual usage metrics and contribution to organization limits.
 */

import React, { useEffect } from 'react';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { UsageProgressBar } from '@/components/subscriptions/UsageProgressBar';
import { UsageLimitBanner } from '@/components/subscriptions/UsageLimitBanner';
import { FileText, Clock, Activity, Calendar, AlertTriangle, TrendingUp } from 'lucide-react';

const MyUsagePage: React.FC = () => {
  const { personalUsage, personalUsageLoading, fetchPersonalUsage } = useSubscriptionStore();

  useEffect(() => {
    fetchPersonalUsage();
  }, []);

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (personalUsageLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!personalUsage) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Usage Data Available</h3>
        <p className="text-gray-500">
          Your organization may not have an active subscription, or no usage has been recorded yet.
        </p>
      </div>
    );
  }

  const { user, currentPeriod, myUsage, orgLimits, orgUsage, recentActivity } = personalUsage;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Usage</h1>
        <p className="text-gray-500 mt-1">
          Track your personal usage and contribution to your organization's limits
        </p>
      </div>

      {/* Alert Banner if org is near limits */}
      <UsageLimitBanner />

      {/* Period Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/80 p-2 rounded-lg">
            <Calendar className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Current Billing Period</p>
            <p className="text-xs text-gray-600">
              {formatDate(currentPeriod.start)} - {formatDate(currentPeriod.end)}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-gray-900">{user.userName}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>
      </div>

      {/* Personal Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* My Encounters - with personal limit */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            {myUsage.personalEncounterLimit > 0 ? (
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                {myUsage.personalEncounterRemaining >= 0
                  ? `${myUsage.personalEncounterRemaining} remaining`
                  : 'Unlimited'}
              </span>
            ) : (
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                {myUsage.percentOfOrgTotal}% of total
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-1">My Encounters</p>
          <p className="text-3xl font-bold text-gray-900">
            {myUsage.encounters.toLocaleString()}
            {myUsage.personalEncounterLimit > 0 && (
              <span className="text-lg text-gray-400 font-normal">
                {' '}/ {myUsage.personalEncounterLimit.toLocaleString()}
              </span>
            )}
          </p>
          {myUsage.personalEncounterLimit > 0 && (
            <div className="mt-3">
              <UsageProgressBar
                used={myUsage.encounters}
                limit={myUsage.personalEncounterLimit}
                label="Personal encounters"
                isUnlimited={myUsage.personalEncounterLimit === -1}
                compact
              />
            </div>
          )}
          {myUsage.proratedFraction < 1 && myUsage.proratedFraction > 0 && (
            <p className="text-xs text-amber-600 mt-2">
              Prorated: {Math.round(myUsage.proratedFraction * 100)}% of full period
            </p>
          )}
          {myUsage.isOverPersonalLimit && myUsage.overagesAllowed && (
            <p className="text-xs text-amber-600 mt-1">
              {myUsage.overageEncounters} overage encounter{myUsage.overageEncounters !== 1 ? 's' : ''} (${(myUsage.overageEncounters * myUsage.overagePrice).toFixed(2)})
            </p>
          )}
          {!myUsage.personalEncounterLimit || myUsage.personalEncounterLimit <= 0 ? (
            <p className="text-xs text-gray-400 mt-2">This billing period</p>
          ) : null}
        </div>

        {/* My Minutes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Clock className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-1">My Transcription Minutes</p>
          <p className="text-3xl font-bold text-gray-900">
            {myUsage.minutes.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Total transcription time
          </p>
        </div>

        {/* Average per Encounter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-100 p-2 rounded-lg">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-1">Avg. Minutes per Encounter</p>
          <p className="text-3xl font-bold text-gray-900">
            {myUsage.encounters > 0
              ? Math.round(myUsage.minutes / myUsage.encounters)
              : 0}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Minutes per appointment
          </p>
        </div>
      </div>

      {/* Organization Usage Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization Usage</h2>
        <p className="text-sm text-gray-500 mb-6">
          Your organization's current usage against subscription limits
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Encounters Progress */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Encounters</span>
              <span className="text-sm text-gray-500">
                {orgLimits.isUnlimited.encounters ? (
                  <span className="text-emerald-600">Unlimited</span>
                ) : (
                  `${orgUsage.encountersUsed.toLocaleString()} / ${orgLimits.encountersPerMonth.toLocaleString()}`
                )}
              </span>
            </div>
            <UsageProgressBar
              used={orgUsage.encountersUsed}
              limit={orgLimits.encountersPerMonth}
              label="Encounters"
              isUnlimited={orgLimits.isUnlimited.encounters}
            />
          </div>

          {/* Minutes Progress */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Transcription Minutes</span>
              <span className="text-sm text-gray-500">
                {orgLimits.isUnlimited.minutes ? (
                  <span className="text-emerald-600">Unlimited</span>
                ) : (
                  `${orgUsage.minutesUsed.toLocaleString()} / ${orgLimits.transcriptionMinutes.toLocaleString()}`
                )}
              </span>
            </div>
            <UsageProgressBar
              used={orgUsage.minutesUsed}
              limit={orgLimits.transcriptionMinutes}
              label="Minutes"
              isUnlimited={orgLimits.isUnlimited.minutes}
            />
          </div>
        </div>

        {/* Overage Warning */}
        {orgUsage.isInOverage && (
          <div className="mt-6 flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Overage Active</p>
              <p className="text-xs text-amber-700">
                Your organization has exceeded the subscription limits. Additional charges may apply.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <p className="text-sm text-gray-500 mt-1">Your last 10 completed encounters</p>
        </div>

        {recentActivity.length === 0 ? (
          <div className="p-8 text-center">
            <Activity className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No recent activity to display</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentActivity.map((activity, index) => (
              <div key={index} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${activity.wasOverage ? 'bg-amber-100' : 'bg-gray-100'}`}>
                    <FileText className={`h-4 w-4 ${activity.wasOverage ? 'text-amber-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Encounter
                      {activity.wasOverage && (
                        <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                          Overage
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDateTime(activity.timestamp)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{activity.minutes} min</p>
                  <p className="text-xs text-gray-500">transcription</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyUsagePage;
